import { formatINR, paiseToRupees } from './money';

/**
 * Gift-card pricing, read straight from the catalogue.
 *
 * ⚠️ Nothing here is calculated. An earlier version derived the face value as
 * `payable ÷ (1 − d)` and that was wrong — not imprecise, wrong. The server
 * clamps a discount that would sell a card below our cost and rounds the clamp
 * with `ceil`, and division cannot undo a ceil. Measured across the live
 * catalogue at the time: 7 of 1,174 rows came out wrong, PhonePe deriving
 * ₹250.01 against a real face of ₹250. If a number is not in the payload, it is
 * not ours to invent.
 *
 * ## The shape, as of 2026-08-07
 *
 * Rupees, not paise — unlike every legacy `display_*` field on the same object:
 *
 *   denominations: [{ face_value, discount_type, discount_value,
 *                     discount_amount, payable_amount }]
 *   denomination_range: { min: {…same…}, max: {…same…} } | null
 *   face_value / discount_amount / payable_amount / discount_label  (card level)
 *
 * `pricePaise` is carried on every option because the purchase and quote
 * endpoints take `denomination` in paise of the *payable* amount. That is the
 * one place the two units meet, and it is converted once, here.
 *
 * The legacy `display_fixed_denomination` / `display_range_denomination` are
 * still read as a fallback. Not belt-and-braces for its own sake: these fields
 * have already appeared, vanished in a rollback, and come back in a different
 * shape within a day, and each time the fallback was what kept a nine-amount
 * card from rendering as a one-amount card.
 */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

/** One `{face_value, discount_amount, payable_amount}` row → our option. */
function toOption(row) {
  const payable = num(row?.payable_amount);
  if (payable == null) return null;

  const face = num(row?.face_value);
  const saving = num(row?.discount_amount) || 0;

  return {
    price: payable,
    // Only a face value that actually differs is worth showing; an equal pair
    // would render as a discount of nothing.
    face: face != null && face > payable ? face : null,
    savings: saving > 0 ? saving : 0,
    // computed — the row publishes both.
    discountType: row?.markup_direction === 'surcharge' ? row?.surcharge_type : row?.discount_type || null,
    discountValue: row?.markup_direction === 'surcharge' ? num(row?.surcharge_value) : num(row?.discount_value),
    label: row?.markup_direction === 'surcharge' ? row?.surcharge_label : row?.discount_label || null,
    pricePaise: Math.round(payable * 100),
  };
}

/**
 * The discount as a rate — "2%" or "₹25" — for a per-option summary line.
 * Null when the row does not publish one; nothing is inferred from the amounts.
 */
export function formatDiscountRate(option) {
  if (!option?.discountValue) return null;
  return option.discountType === 'percentage' || option.discountType === 'percent'
    ? `${Number(option.discountValue.toFixed(2))}%`
    : formatINR(option.discountValue);
}

/**
 * The amounts a fixed-denomination card can be bought at, cheapest first.
 * @returns {{price, face, savings, pricePaise}[]}
 */
export function getRewardDenominations(reward) {
  const rows = reward?.denominations;

  if (Array.isArray(rows) && rows.length) {
    const mapped = rows.map(toOption).filter(Boolean);
    if (mapped.length) return mapped.sort((a, b) => a.price - b.price);
  }

  // Legacy: paise, and no face value — without a discount amount there is
  // nothing saying the price was discounted, and it will not be inferred.
  return (reward?.display_fixed_denomination || [])
    .map((paise) => ({
      price: paiseToRupees(paise),
      face: null,
      savings: 0,
      pricePaise: paise,
    }))
    .sort((a, b) => a.price - b.price);
}

/**
 * A free-entry card's bounds, or null.
 *
 * Both scales, because the two are used for different things: the customer
 * types a *face* value — that is what a gift card is worth and what the
 * recipient sees — while `denomination` on every purchase endpoint is the
 * *payable* amount.
 *
 * @returns {{minFace, maxFace, minPayable, maxPayable, discountType, discountValue}|null}
 */
export function getRewardRange(reward) {
  const range = reward?.denomination_range;
  const min = toOption(range?.min);
  const max = toOption(range?.max);

  if (min && max) {
    return {
      minFace: min.face ?? min.price,
      maxFace: max.face ?? max.price,
      minPayable: min.price,
      maxPayable: max.price,
      discountType: min.discountType,
      discountValue: min.discountValue,
    };
  }

  const legacy = reward?.display_range_denomination;
  if (Array.isArray(legacy) && legacy.length >= 2) {
    const lo = paiseToRupees(legacy[0]);
    const hi = paiseToRupees(legacy[1]);
    return {
      minFace: lo, maxFace: hi, minPayable: lo, maxPayable: hi,
      discountType: null, discountValue: null,
    };
  }
  return null;
}

/**
 * The payable amount to *ask the server about* for a given face value.
 *
 * This is the one calculation in the file and it is deliberately not a display
 * value. `denomination` on the quote and purchase endpoints is the payable
 * amount, and there is no face-based lookup, so a face the customer typed has
 * to be turned into a payable to form the request. Whatever comes back — face,
 * discount and payable — is what gets shown; if the server rounds differently
 * by a paisa, the server wins and the screen says so.
 *
 * The banned direction is the reverse: recovering a face from a payable, which
 * cannot survive the below-cost clamp's ceil.
 */
export function payableForFace(faceValue, range) {
  const face = num(faceValue);
  if (face == null || face <= 0 || !range?.discountValue) return face;

  const off = range.discountType === 'percentage' || range.discountType === 'percent'
    ? face * (range.discountValue / 100)
    : range.discountValue;

  return Math.max(0, face - off);
}

/**
 * Card-level pricing, for the catalogue tile and the page headline.
 * @returns {{price, face, savings, label}|null}
 */
export function getRewardPricing(reward) {
  if (!reward) return null;

  const payable = num(reward.payable_amount);
  if (payable != null) {
    const face = num(reward.face_value);
    const saving = num(reward.discount_amount) || 0;
    return {
      price: payable,
      face: face != null && face > payable ? face : null,
      savings: saving > 0 ? saving : 0,
      label: reward.markup_direction === 'surcharge' ? reward.surcharge_label : (reward.discount_label || null),
    };
  }

  // Legacy paise fields, still published alongside.
  const price = num(reward.display_price);
  if (price == null) return null;
  const savings = num(reward.savings) || 0;
  return {
    price: paiseToRupees(price),
    face: savings > 0 ? paiseToRupees(reward.face_price) : null,
    savings: paiseToRupees(savings),
    label: reward.discount_label || null,
  };
}
